import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { installAccessTokenProvider } from '../api/client';
import { useMe } from '../api/hooks/useMe';
import { sitevars } from '../config/sitevars';
import { useLocalPasswordSession } from './localPasswordSession';

const MOCK_AUTH_ENABLED = import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === 'true';

export function AuthFailure({ onRetry }: { onRetry: () => void }) {
  return <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center" role="alert">
    <h1 className="text-lg font-medium">We couldn’t verify your sign-in</h1>
    <p>Please try signing in again.</p>
    <button type="button" onClick={onRetry} className="rounded border px-4 py-2">Sign in again</button>
  </main>;
}

function SessionStatus({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
    <p className="text-sm font-medium text-muted-foreground">{children}</p>
  </div>;
}

function VerifiedIdentity({ children, onRetry }: { children: ReactNode; onRetry: () => void }) {
  const me = useMe();
  if (me.isError) return <AuthFailure onRetry={onRetry} />;
  if (!me.isSuccess) return <SessionStatus>Verifying your account…</SessionStatus>;
  return <>{children}</>;
}

function TokenSession({ children, onRetry, getToken }: { children: ReactNode; onRetry: () => void; getToken: () => Promise<string> }) {
  const queryClient = useQueryClient();
  const [ready, setReady] = useState(false);
  useLayoutEffect(() => {
    const revoke = installAccessTokenProvider(getToken);
    // A layout-phase render is necessary: descendant query effects must not
    // run until this external Axios session has been registered.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setReady(true);
    return () => {
      revoke();
      queryClient.clear();
    };
  }, [getToken, queryClient]);
  // Effects run child-first. Mount queries only after registering the SDK getter.
  if (!ready) return <SessionStatus>Checking your session…</SessionStatus>;
  return <VerifiedIdentity onRetry={onRetry}>{children}</VerifiedIdentity>;
}

function AuthenticatedSession({ children, onRetry }: { children: ReactNode; onRetry: () => void }) {
  const { getAccessTokenSilently } = useAuth0();
  const getToken = useCallback(() => getAccessTokenSilently(), [getAccessTokenSilently]);
  return <TokenSession getToken={getToken} onRetry={onRetry}>{children}</TokenSession>;
}

function Auth0ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, error, user, loginWithRedirect } = useAuth0();
  const [redirectFailed, setRedirectFailed] = useState(false);
  const redirectStarted = useRef(false);
  const signIn = () => {
    setRedirectFailed(false);
    void loginWithRedirect({
      appState: { returnTo: `${window.location.pathname}${window.location.search}` },
    }).catch(() => setRedirectFailed(true));
  };
  useEffect(() => {
    if (MOCK_AUTH_ENABLED || isLoading || isAuthenticated || error || redirectStarted.current) return;
    redirectStarted.current = true;
    void loginWithRedirect({
      appState: { returnTo: `${window.location.pathname}${window.location.search}` },
    }).catch(() => setRedirectFailed(true));
  }, [isLoading, isAuthenticated, error, loginWithRedirect]);

  if (MOCK_AUTH_ENABLED) return <>{children}</>;
  if (error || redirectFailed) return <AuthFailure onRetry={signIn} />;
  if (isLoading) return <SessionStatus>Checking your session…</SessionStatus>;
  if (!isAuthenticated) return <SessionStatus>Redirecting to sign in…</SessionStatus>;
  if (!user?.sub) return <AuthFailure onRetry={signIn} />;
  return <AuthenticatedSession key={user.sub} onRetry={signIn}>{children}</AuthenticatedSession>;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const localSession = useLocalPasswordSession();
  const localToken = localSession?.token ?? null;
  const getLocalToken = useCallback(async () => localToken ?? '', [localToken]);
  if (sitevars.localPasswordAuth && localToken) {
    return (
      <TokenSession getToken={getLocalToken} onRetry={() => localSession?.setToken(null)}>
        {children}
      </TokenSession>
    );
  }
  if (sitevars.localPasswordAuth && !sitevars.auth0Domain) {
    return <Navigate to="/login" replace />;
  }
  return <Auth0ProtectedRoute>{children}</Auth0ProtectedRoute>;
}
